import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import {
  useQuiz,
  useDeleteQuiz,
  useDeleteQuestion,
  deleteQuizErrorMessage,
} from '@/api/course-builder';
import { ErrorMessage } from '@/components/shared/error-message';
import { FormShell } from '@/components/shared/form-shell';
import { AddQuestionForm } from './add-question-form';
import { QTYPE_LABELS, btnSecondary } from './constants';

type ManageQuizProps = {
  quiz: NonNullable<ReturnType<typeof useQuiz>['data']>;
  courseId: number;
  onClose: () => void;
};

export function ManageQuiz({ quiz, courseId, onClose }: ManageQuizProps) {
  const deleteQuiz = useDeleteQuiz();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteQuestion = useDeleteQuestion();

  const handleDeleteQuiz = async () => {
    if (!confirm('Eliminar esta evaluación y todas sus preguntas?')) return;
    setDeleteError(null);
    try {
      await deleteQuiz.mutateAsync({ quizId: quiz.id, courseId });
      onClose();
    } catch (err) {
      setDeleteError(deleteQuizErrorMessage(err));
    }
  };

  return (
    <FormShell
      title="Administrar evaluación"
      onClose={onClose}
      className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border bg-card p-6 shadow-lg"
    >
      {deleteError && (
        <div className="mb-4">
          <ErrorMessage message={deleteError} />
        </div>
      )}

      {/* Quiz info header */}
      <div className="mb-4 rounded-lg border border-border bg-muted/50 p-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground">{quiz.title}</h4>
          <button
            onClick={handleDeleteQuiz}
            disabled={deleteQuiz.isPending}
            className="rounded p-1 text-destructive hover:bg-destructive/10"
            aria-label="Eliminar evaluación"
            title="Eliminar evaluación"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Aprobacion: {quiz.passing_percent}% | Intentos: {quiz.max_attempts}
          {quiz.time_limit_minutes ? ` | Tiempo: ${quiz.time_limit_minutes} min` : ''}
        </p>
      </div>

      {/* Questions list */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">
          Preguntas ({quiz.questions?.length ?? 0})
        </h4>
        {quiz.questions?.map((q, i) => (
          <div key={q.id} className="rounded-lg border border-border p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">
                  {i + 1}. {q.content}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {QTYPE_LABELS[q.question_type]} | {q.points} pts
                </p>
                {q.choices?.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {q.choices.map((c) => (
                      <li
                        key={c.id}
                        className={`text-xs ${c.is_correct ? 'font-semibold text-emerald-600' : 'text-muted-foreground'}`}
                      >
                        {c.is_correct ? '✓' : '○'} {c.content}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                onClick={async () => {
                  try {
                    await deleteQuestion.mutateAsync({
                      questionId: q.id,
                      quizId: quiz.id,
                      courseId,
                    });
                  } catch {
                    void 0;
                  }
                }}
                disabled={deleteQuestion.isPending}
                className="rounded p-1 text-destructive hover:bg-destructive/10"
                aria-label="Eliminar pregunta"
                title="Eliminar pregunta"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add question form */}
      <AddQuestionForm quizId={quiz.id} courseId={courseId} />

      <div className="mt-4 flex justify-end">
        <button onClick={onClose} className={btnSecondary}>
          Cerrar
        </button>
      </div>
    </FormShell>
  );
}
